import {
  Typography,
  Button,
  Space,
  Timeline,
  Divider,
  Tag,
  Alert,
  Tooltip,
} from "antd";
import {
  CloseOutlined,
  DownloadOutlined,
  ShareAltOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { VaultFile } from "@/types";
import {
  formatFileSize,
  formatFullDate,
  getRelativeTimeString,
  formatDeletionCountdown,
  getDaysUntilDeletion,
  getDeletionUrgency,
  formatDateOnly,
} from "@/utils/formatters";
import { ACTIVITY_ACTIONS } from "@/utils/constants";
import { useFileActivity } from "@/hooks/useAuditLog";

const { Title, Text } = Typography;

// Returns color based on file MIME type
function getFileColor(mimeType: string): string {
  if (mimeType.startsWith("image/")) return "#8B5CF6"; // Purple for images
  if (mimeType.startsWith("video/")) return "#EC4899"; // Pink for videos
  if (mimeType.startsWith("audio/")) return "#F97316"; // Orange for audio
  if (mimeType === "application/pdf") return "#EF4444"; // Red for PDFs
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel")) return "#10B981"; // Green for spreadsheets
  if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return "#F59E0B"; // Amber for presentations
  if (mimeType.includes("word") || mimeType.includes("document")) return "#3B82F6"; // Blue for documents
  if (mimeType.startsWith("text/")) return "#6B7280"; // Gray for text files
  if (mimeType.includes("zip") || mimeType.includes("compressed") || mimeType.includes("archive")) return "#64748B"; // Slate for archives
  return "#0F6E56"; // Primary green for others
}

interface DetailPanelProps {
  file: VaultFile;
  onClose: () => void;
  onDownload: () => void;
  onShare: () => void;
  canShare?: boolean;
}

interface MetadataRowProps {
  label: React.ReactNode;
  value: React.ReactNode;
  highlight?: boolean;
}

function MetadataRow({ label, value, highlight }: MetadataRowProps) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 0",
        borderBottom: "1px solid #F3F4F6",
      }}
    >
      <div style={{ color: "#6B7280", fontSize: 13 }}>{label}</div>
      <Text
        style={{
          fontSize: 13,
          fontWeight: highlight ? 600 : 400,
          color: highlight ? "#EF4444" : "#1F2937",
        }}
      >
        {value}
      </Text>
    </div>
  );
}

export default function DetailPanel({
  file,
  onClose,
  onDownload,
  onShare,
  canShare = true,
}: DetailPanelProps) {
  const { activities, isLoading: activitiesLoading } = useFileActivity(file.id);

  // Auto-deletion info
  const daysLeft = file.scheduledDeletionAt ? getDaysUntilDeletion(file.scheduledDeletionAt) : null;
  const urgency = file.scheduledDeletionAt ? getDeletionUrgency(file.scheduledDeletionAt) : "normal";
  const deletionText = file.scheduledDeletionAt ? formatDeletionCountdown(file.scheduledDeletionAt) : null;

  const getAlertType = () => {
    if (urgency === "critical") return "error";
    if (urgency === "warning") return "warning";
    return "info";
  };

  const getTimelineItems = () => {
    return activities.map((activity) => {
      const actionText = ACTIVITY_ACTIONS[activity.action] || activity.action;
      const hasEmail = activity.performedBy?.email;

      const nameElement = hasEmail ? (
        <Tooltip title={activity.performedBy.email} placement="top">
          <Text strong style={{ cursor: "pointer", borderBottom: "1px dashed #D1D5DB" }}>
            {activity.performedBy.name}
          </Text>
        </Tooltip>
      ) : (
        <Text strong>{activity.performedBy?.name || "Unknown"}</Text>
      );

      return {
        key: activity.id,
        children: (
          <>
            <Text style={{ fontSize: 12 }}>
              {nameElement} {actionText}
            </Text>
            <br />
            <Tooltip title={formatFullDate(activity.performedAt)} placement="top">
              <Text type="secondary" style={{ fontSize: 11, cursor: "default" }}>
                {getRelativeTimeString(activity.performedAt)}
              </Text>
            </Tooltip>
          </>
        ),
      };
    });
  };

  const fileExtension = file.mimeType.split("/")[1]?.toUpperCase() || "FILE";

  return (
    <div
      className="detail-panel"
      style={{
        width: 340,
        padding: 24,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "#ffffff",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <Title level={5} style={{ margin: 0, fontSize: 16 }}>
          File details
        </Title>
        <Button
          type="text"
          icon={<CloseOutlined style={{ fontSize: 14 }} />}
          onClick={onClose}
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      </div>

      {/* Auto-deletion warning */}
      {deletionText && daysLeft !== null && daysLeft <= 7 && (
        <Alert
          type={getAlertType()}
          showIcon
          icon={<ClockCircleOutlined />}
          message={deletionText}
          description={
            urgency === "critical"
              ? "Download or share before it expires."
              : "Auto-deleted after 30 days for security."
          }
          style={{ marginBottom: 20, borderRadius: 10 }}
        />
      )}

      {/* Preview area */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "28px 20px",
          background: "#F9FAFB",
          borderRadius: 12,
          marginBottom: 24,
          border: "1px solid #E5E7EB",
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 10,
            background: `${getFileColor(file.mimeType)}15`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 14,
          }}
        >
          <span style={{ fontSize: 20, fontWeight: 600, color: getFileColor(file.mimeType) }}>
            {fileExtension.slice(0, 4)}
          </span>
        </div>
        <Text
          strong
          style={{
            fontSize: 14,
            textAlign: "center",
            wordBreak: "break-word",
            lineHeight: 1.4,
            maxWidth: "100%",
            color: "#1F2937",
          }}
        >
          {file.fileName}
        </Text>
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={onDownload}
          style={{ flex: 1, height: 44, borderRadius: 10, fontWeight: 500 }}
        >
          Download
        </Button>
        {canShare && (
          <Button
            icon={<ShareAltOutlined />}
            onClick={onShare}
            style={{ flex: 1, height: 44, borderRadius: 10, fontWeight: 500 }}
          >
            Share
          </Button>
        )}
      </div>

      {/* Metadata */}
      <div style={{ marginBottom: 20 }}>
        <MetadataRow label="Size" value={formatFileSize(file.size)} />
        <MetadataRow label="Type" value={fileExtension} />
        <MetadataRow label="Uploaded" value={formatFullDate(file.uploadedAt)} />
        <MetadataRow
          label="Created by"
          value={
            <Tooltip title={file.uploadedBy.email} placement="top">
              <span style={{ cursor: "pointer", borderBottom: "1px dashed #D1D5DB" }}>
                {file.uploadedBy.name}
              </span>
            </Tooltip>
          }
        />
        <MetadataRow label="Location" value={file.folderId ? "Folder" : "Root"} />
        {file.scheduledDeletionAt && (
          <MetadataRow
            label={
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                Auto-deletes
                <Tooltip
                  title="For security, shared files are automatically deleted after 30 days. Download important files before expiration."
                  placement="top"
                >
                  <InfoCircleOutlined style={{ fontSize: 12, color: "#9CA3AF", cursor: "help" }} />
                </Tooltip>
              </span>
            }
            value={`${formatDateOnly(file.scheduledDeletionAt)} (${daysLeft}d)`}
            highlight={urgency !== "normal"}
          />
        )}
      </div>

      {/* Tags */}
      {file.tags.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Text style={{ color: "#6B7280", fontSize: 13, display: "block", marginBottom: 10 }}>
            Tags
          </Text>
          <Space wrap size={8}>
            {file.tags.map((tag) => (
              <Tag
                key={tag}
                style={{
                  borderRadius: 6,
                  padding: "4px 10px",
                  background: "#F3F4F6",
                  border: "none",
                  color: "#4B5563",
                }}
              >
                {tag}
              </Tag>
            ))}
          </Space>
        </div>
      )}

      <Divider style={{ margin: "8px 0 16px 0" }} />

      {/* Activity */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <Text
          style={{
            color: "#6B7280",
            fontSize: 13,
            display: "block",
            paddingBottom: 14,
            background: "#ffffff",
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          Recent activity
        </Text>
        <div style={{ flex: 1, overflow: "auto" }}>
          {activitiesLoading ? (
            <Text type="secondary" style={{ fontSize: 13 }}>Loading...</Text>
          ) : activities.length > 0 ? (
            <Timeline items={getTimelineItems()} />
          ) : (
            <Text type="secondary" style={{ fontSize: 13 }}>No recent activity</Text>
          )}
        </div>
      </div>
    </div>
  );
}
